import { useMemo } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { DataTable, type Column } from '@/components/DataTable'
import { FilterPanel, SelectFilter } from '@/components/FilterPanel'
import { SearchInput } from '@/components/SearchInput'
import { StatusBadge } from '@/components/StatusBadge'
import { IconOrganizations } from '@/components/Icons'
import { useOrganizations } from '@/hooks/useRootQueries'
import type { Organization } from '@/types/api'
import { relativeTime, exactTimestamp } from '@/utils/time'

export function Organizations() {
  const navigate = useNavigate()
  const [params, setParams] = useSearchParams()

  const filters = useMemo(
    () => ({
      search: params.get('search') ?? '',
      status: params.get('status') ?? '',
      page: Number(params.get('page') ?? '1'),
      pageSize: 25,
    }),
    [params],
  )

  function updateParam(key: string, value: string) {
    const next = new URLSearchParams(params)
    if (value) next.set(key, value)
    else next.delete(key)
    if (key !== 'page') next.set('page', '1')
    setParams(next)
  }

  const orgs = useOrganizations(filters)

  const columns: Column<Organization>[] = [
    {
      key: 'name',
      header: 'Organization',
      render: (row) => (
        <div className="flex items-center gap-3">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-sky-50 text-sky-700 font-bold border border-sky-200 shadow-2xs">
            <IconOrganizations className="h-4 w-4" />
          </div>
          <div>
            <div className="text-slate-900 font-bold text-xs">{row.name}</div>
            <div className="font-mono text-[10.5px] text-slate-500">{row.slug}</div>
          </div>
        </div>
      ),
    },
    {
      key: 'id',
      header: 'Organization ID',
      render: (row) => (
        <span className="font-mono text-[11px] text-slate-500 select-all">{row.id}</span>
      ),
    },
    { key: 'status', header: 'Status', render: (row) => <StatusBadge status={row.status} /> },
    {
      key: 'createdAt',
      header: 'Created Date',
      render: (row) => (
        <div>
          <div className="font-semibold text-slate-900">{relativeTime(row.createdAt)}</div>
          <div className="font-mono text-[10px] text-slate-400">{exactTimestamp(row.createdAt)}</div>
        </div>
      ),
    },
    {
      key: 'actions',
      header: '',
      render: (row) => (
        <button
          onClick={(e) => {
            e.stopPropagation()
            navigate(`/organizations/${row.id}`)
          }}
          className="rounded-lg border border-slate-200 bg-white px-2.5 py-1 font-mono text-[11px] font-semibold text-slate-700 hover:border-sky-300 hover:bg-sky-50 hover:text-sky-700 shadow-2xs transition-all"
        >
          Manage →
        </button>
      ),
    },
  ]

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-lg font-bold text-slate-900 tracking-tight">Organizations &amp; Tenants</h1>
          <p className="mt-0.5 text-xs font-medium text-slate-500">
            Multi-tenant organization accounts, active status, and resource usage.
          </p>
        </div>
      </div>

      <FilterPanel>
        <div className="min-w-[220px] flex-1">
          <span className="mb-1 block text-[10.5px] font-bold text-slate-500 uppercase">Search Organizations</span>
          <SearchInput value={filters.search} onChange={(v) => updateParam('search', v)} placeholder="Name, slug, or ID…" />
        </div>
        <SelectFilter
          label="Status"
          value={filters.status}
          options={[
            { value: 'active', label: 'Active' },
            { value: 'suspended', label: 'Suspended' },
          ]}
          onChange={(v) => updateParam('status', v)}
        />
        {(filters.search || filters.status) && (
          <button
            onClick={() => setParams(new URLSearchParams())}
            className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-50 hover:text-slate-900 shadow-xs"
          >
            Reset
          </button>
        )}
      </FilterPanel>

      <DataTable
        columns={columns}
        rows={orgs.data?.data ?? []}
        getRowId={(row) => row.id}
        isLoading={orgs.isLoading}
        isError={orgs.isError}
        errorMessage="Could not load organizations."
        onRetry={() => orgs.refetch()}
        emptyTitle="No organizations found"
        emptyDescription="Try a different search term or filter."
        onRowClick={(row) => navigate(`/organizations/${row.id}`)}
        page={filters.page}
        totalPages={orgs.data?.meta.totalPages ?? 1}
        totalItems={orgs.data?.meta.totalItems ?? 0}
        onPageChange={(p) => updateParam('page', String(p))}
      />
    </div>
  )
}
